import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {connect} from 'react-redux';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  Grid,
  AccessibleGridCell,
  isFullyAccessibleGrid,
  FullyAccessibleGrid,
} from 'constants/SampleConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FillDirection} from 'lookup-config/LookupConfigModalConstants';
import * as CreateContainerModal from 'samples/containers/CreateContainerModal';
import FillDirectionModal from 'samples/grids/FillDirectionModal';
import {getGridTypeName} from 'samples/grids/GridUtil';
import {openTransferPlatesModal} from 'samples/grids/TransferPlatesModal';
import UploadContainersFlow from 'samples/grids/upload-containers/UploadContainersFlow';
import {
  closeUploadContainersFlow,
  CloseUploadContainersFlowAction,
  openUploadContainersFlow,
  OpenUploadContainersFlowAction,
} from 'samples/grids/upload-containers/UploadContainersReduxActions';
import * as SampleUtil from 'samples/SampleUtil';
import * as TransferUtil from 'samples/transfers/TransferUtil';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option, {OptionProps} from 'ui-library/menus/Option';
import * as ModalContainer from 'ui-library/ModalContainer';
import APIUtil from 'util/APIUtil';

export interface GridActionsDropdownProps {
  cells: Array<AccessibleGridCell>;
  grid: Grid;
  onSubmitScanToFillModal: (fillDirection: FillDirection) => void;
  closeUploadContainersFlow: () => CloseUploadContainersFlowAction;
  openUploadContainersFlow: () => OpenUploadContainersFlowAction;
}

export class GridActionsDropdown extends React.Component<GridActionsDropdownProps> {
  private getActionOrigin = (): ActionOrigin =>
    APIUtil.apiIdToType(this.props.grid.id) === ModelTypeConstants.PLATE
      ? ActionOrigin.INVENTORY_PLATE_PAGE_GRID_CELL_TOOLBAR
      : ActionOrigin.INVENTORY_BOX_PAGE_GRID_CELL_TOOLBAR;

  private handleOpenCreateContainers = (): void => {
    CreateContainerModal.open({
      initialParentStorableId: this.props.grid.id,
      initialRegistry: null,
      actionOrigin: this.getActionOrigin(),
    });
  };

  private handleTransferPlate = (): void => {
    openTransferPlatesModal({
      cells: this.props.cells,
      // Transfer plate option is disabled if grid is not fully accessible (all containers accessible)
      grid: this.props.grid as FullyAccessibleGrid,
      registryId: this.props.grid.storableSchema.registryId,
    });
  };

  private handleOpenScanToFillGrid = (): void => {
    ModalContainer.open(
      FillDirectionModal,
      <FillDirectionModal
        gridTypeName={getGridTypeName(this.props.grid.id)}
        onRequestClose={() => {
          ModalContainer.close(FillDirectionModal);
        }}
        onSubmit={(fillDirection: FillDirection) => {
          this.props.onSubmitScanToFillModal(fillDirection);
          ModalContainer.close(FillDirectionModal);
        }}
      />
    );
  };

  render(): JSX.Element {
    const allowTransfers = APIUtil.apiIdToType(this.props.grid.id) === ModelTypeConstants.PLATE;
    const hasCells = this.props.cells.length > 0;
    const gridIsFullyAccessible = isFullyAccessibleGrid(this.props.grid);
    const {contentsAreValid} = allowTransfers
      ? TransferUtil.validateTransferInputContents(
          this.props.cells.map(({container}) => ({
            ...container,
            containedItemIds: container.contents.map(({containedItemId}) => containedItemId),
          }))
        )
      : {contentsAreValid: true};
    const isFixedPlate = SampleUtil.isFixedPlate(this.props.grid);
    const options: Array<{visible: boolean; key: string; props: Partial<OptionProps<string>>}> = [
      {
        visible: !isFixedPlate,
        key: 'Create containers',
        props: {
          icon: 'compose',
          onSelect: this.handleOpenCreateContainers,
          dataTestAttrs: {component: 'GridActionsDropdown', key: 'createContainers'},
        },
      },
      {
        visible: !isFixedPlate,
        key: 'Set from matrix scanner',
        props: {
          icon: 'scan',
          onSelect: this.props.openUploadContainersFlow,
          dataTestAttrs: {component: 'GridActionsDropdown', key: 'setFromMatrixScanner'},
        },
      },
      {
        visible: !isFixedPlate,
        key: `Scan to fill ${getGridTypeName(this.props.grid.id)}`,
        props: {
          icon: 'scanner-gun',
          onSelect: this.handleOpenScanToFillGrid,
          dataTestAttrs: {component: 'GridActionsDropdown', key: 'scanToFillGrid'},
        },
      },
      {
        visible: allowTransfers && hasCells,
        key: 'Stamp plate',
        props: {
          icon: ModelTypeConstants.PLATE,
          onSelect: this.handleTransferPlate,
          isDisabled: !contentsAreValid || !gridIsFullyAccessible,
          tooltipTitle: !contentsAreValid
            ? TransferUtil.getMultipleTransferInputContentsErrorText(isFixedPlate)
            : !gridIsFullyAccessible
              ? 'All containers must be accessible to stamp plate.'
              : undefined,
          dataTestAttrs: {component: 'GridActionsDropdown', key: 'stampPlate'},
        },
      },
    ];
    const menu = (
      <Menu dropLeft={true} height={MenuHeight.LARGE} width={MenuWidth.LARGE}>
        {options.map(
          (item) =>
            item.visible && (
              <Option {...item.props} key={item.key} contentClassName={css(styles.optionText)}>
                {item.key}
              </Option>
            )
        )}
      </Menu>
    );
    return (
      <div className="buttonGroup">
        <UploadContainersFlow grid={this.props.grid} onRequestClose={this.props.closeUploadContainersFlow} />
        <Dropdown menu={menu} closeOnInsideClick={true} closeOnOutsideClick={true}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            isDropdown={true}
            size={ButtonSize.SMALL}
            text="Actions"
            dataTestAttrs={{component: 'GridActionsDropdown', element: 'actionsButton'}}
          />
        </Dropdown>
      </div>
    );
  }
}

const styles = StyleSheet.create({
  optionText: {
    fontSize: '13px',
  },
});

export default connect(null, {
  closeUploadContainersFlow,
  openUploadContainersFlow,
})(GridActionsDropdown);
